import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ApexAxisChartSeries, ApexChart, ApexXAxis, ApexDataLabels, ApexGrid, ApexStroke, ApexTitleSubtitle, ChartComponent, ApexYAxis, ApexResponsive, ApexTooltip } from 'ng-apexcharts';
import { BuySellSerie, ClientCompanyToDebtorRelationSummaryDTO, InvoiceRelationDTO } from '../../interface/company-to-debtor.interface';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

export type ChartOptions = {
  colors: string[];
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  tooltip: ApexTooltip;
  responsive: ApexResponsive[];
};

@Component({
  selector: 'app-client-to-debtor-relationship-summary',
  templateUrl: './client-to-debtor-relationship-summary.component.html',
  styleUrls: ['./client-to-debtor-relationship-summary.component.scss']
})
export class ClientToDebtorRelationshipSummaryComponent implements OnInit, AfterViewInit {
  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;

  @ViewChild('chartDiv') chartDiv: ElementRef;

  public dataSource: MatTableDataSource<InvoiceRelationDTO>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  public displayColumns = [
    'folio',
    'clientRut',
    'clientName',
    'debtorRut',
    'debtorName',
    'fechaDocto',
    'montoTotal',
    'isCedida',
    'cesionarioRut',
    'cesionarioName',
    'fechaCesion',
    'montoCesion',
  ];

  /**
   * Tipo de dato de las columnas
   */
  columnTypes = {
    folio: 'string',
    clientRut: 'string',
    clientName: 'string',
    debtorRut: 'string',
    debtorName: 'string',
    montoTotal: 'currency',
    fechaDocto: 'date',
    fechaCesion: 'date',
    montoCesion: 'currency',
    cesionarioRut: 'string',
    cesionarioName: 'string',
    isCedida: 'boolean'
  };

  /**
   * Nombre de los encabezados de tabla
   */
  columnHeaders = {
    folio: 'Folio',
    clientRut: 'RUT Cliente',
    clientName: 'Nombre Cliente',
    debtorRut: 'RUT Deudor',
    debtorName: 'Nombre Deudor',
    montoTotal: 'Monto Total',
    fechaDocto: 'Fecha Docto',
    fechaCesion: 'Fecha Cesión',
    montoCesion: 'Monto Cedido',
    cesionarioRut: 'RUT Cesionario',
    cesionarioName: 'Nombre Cesionario',
    isCedida: 'Cedido'
  }

  /**
   * Input de componente que recibe la informacion de la relacion del cliente y deudor
   */
  @Input()
  public clientDebtorSummary: ClientCompanyToDebtorRelationSummaryDTO | null;

  constructor(){
    this.dataSource = new MatTableDataSource([]);
    this.chartOptions = {
      colors: ['#508222', '#c21919'],
      chart: {        
        toolbar: {
          show: false
        },
        locales: [{
          name: 'es',
          options: {
            months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
            shortMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
          }
        }],
        defaultLocale: 'es',
        height: 400,        
        type: 'bar',
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "smooth",
        width: 1
      },
      title: {
        text: "Histórico Compra/Venta",
        align: "left"
      },
      grid: {
        row: {
          colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
          opacity: 0.5
        }
      },
      yaxis: {
        labels: {
          formatter: function(val) {
            return '$'+ val.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0});
          }
        },
      },
      tooltip: {
        followCursor: true,
        shared: true,
        intersect: false,  
        y: {
          formatter: function(value) {
            return value?.toLocaleString('es-CL') || '';
          }
        }
      },
      responsive: [
        {
          breakpoint: 680,
          options:{
            xaxis: {
              labels:{
                rotate: -45
              },
              tickAmount: 3
            },
            yaxis: {
              labels:{
                formatter: function(value) {
                  return formatNumber(value);
                }
              }
            }
          }
        }
      ]
    };
  }

  ngOnInit() {
    console.log(this.clientDebtorSummary);
    this.setRelationInvoicesDataSource(this.clientDebtorSummary.invoices);
    this.setHistoricChart(this.clientDebtorSummary?.historicBuySell);
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setChartWidth();
  }

  public chartWidth: number;
  public setChartWidth(): void{
    const points = this.clientDebtorSummary?.historicBuySell.length;
    const width =  this.chartDiv.nativeElement.offsetWidth;
    const widthPerPoint = 50;
    const calculatedWidth = Math.max(width, points * widthPerPoint);
    this.chartOptions = {
      ...this.chartOptions,
      chart: {
        ...this.chartOptions.chart,
        width: calculatedWidth
      }
    };

  }

  private setRelationInvoicesDataSource(data: Array<InvoiceRelationDTO>){
    this.dataSource.data = data;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private setHistoricChart(historic: Array<BuySellSerie>) {
    const sells = historic.map(h => h.sell);
    const buys = historic.map(h => h.buy);
    const dates = historic.map(h => h.monthYear);
    this.chartOptions = {
      ...this.chartOptions,
      chart: {
        ...this.chartOptions.chart,
      },
      series: [
        {
          name: 'Ventas',
          data: sells,
        },
        {
          name: 'Compras',
          data: buys
        }
      ],
      xaxis: {
        type: 'category',
        categories: dates,
        labels: {
          rotate: -45
        },
        tickAmount: 7
      },
    }
  }
}

function formatNumber(value: any) {
  if(value >= 1000000)
    {
      return (value / 1000000).toFixed(1) + 'M';
    }
    return value;
}